import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import docsContent from "./docsContent.json";

const RenderBody = ({ body }) => {
  if (!body) return null;
  return (
    <>
      {body.map((block, i) => {
        if (block.type === "paragraph") {
          return (
            <p key={i} className="text-[#cccccc] text-sm leading-relaxed mb-4">
              {block.text}
            </p>
          );
        }
        if (block.type === "list") {
          return (
            <ul key={i} className="mb-4 space-y-1.5">
              {block.items.map((item, j) => (
                <li
                  key={j}
                  className="flex items-start gap-2 text-[#cccccc] text-sm"
                >
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#cccccc] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          );
        }
        if (block.type === "ordered-list") {
          return (
            <ol key={i} className="mb-4 space-y-1.5 list-none">
              {block.items.map((item, j) => (
                <li
                  key={j}
                  className="flex items-start gap-2 text-[#cccccc] text-sm"
                >
                  <span className="flex-shrink-0 text-[#cccccc]">{j + 1}.</span>
                  {item}
                </li>
              ))}
            </ol>
          );
        }
        if (block.type === "role-block") {
          return (
            <div key={i} className="mb-4">
              <p className="text-white font-semibold text-sm mb-1">
                {block.role}
              </p>
              {block.description && (
                <p className="text-[#cccccc] text-sm mb-2">
                  {block.description}
                </p>
              )}
              {block.ordered ? (
                <ol className="space-y-1.5 list-none">
                  {block.items.map((item, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-2 text-[#cccccc] text-sm"
                    >
                      <span className="flex-shrink-0">{j + 1}.</span>
                      {item}
                    </li>
                  ))}
                </ol>
              ) : (
                <ul className="space-y-1.5">
                  {block.items.map((item, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-2 text-[#cccccc] text-sm"
                    >
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#cccccc] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        }
if (block.type === "image") {
  return (
    <div key={i} className="my-6 flex justify-center">
      <img
        src={block.src}
        alt={block.alt}
        style={{ maxWidth: "720px", width: "100%", height: "auto", borderRadius: "8px" }}
      />
    </div>
  );
}
        return null;
      })}
    </>
  );
};

const Docs = () => {
  const { sections, content } = docsContent;
  const navigate = useNavigate();

  // activeSection = top-level section id
  const [activeSection, setActiveSection] = useState("getting-started");
  // activeSub = subsection id (or null)
  const [activeSub, setActiveSub] = useState("how-the-system-works");
  // expanded sidebar sections
  const [expanded, setExpanded] = useState({ "getting-started": true });
  // mobile sidebar open/close
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 967);

useEffect(() => {
  const handleResize = () => setIsDesktop(window.innerWidth > 967);
  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);

  const contentRef = useRef(null);
  const sectionRefs = useRef({});
  const observerRef = useRef(null);
  const isScrollingTo = useRef(false);

  const allAnchors = sections.flatMap((sec) => {
    const subs = (sec.subsections || []).map((sub) => sub.id);
    return [sec.id, ...subs];
  });

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (isScrollingTo.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length === 0) return;

        const topId = visible[0].target.id;

        for (const sec of sections) {
          if (sec.id === topId) {
            setActiveSection(sec.id);
            setActiveSub(null);
            setExpanded((prev) => ({ ...prev, [sec.id]: true }));
            return;
          }
          const sub = (sec.subsections || []).find((s) => s.id === topId);
          if (sub) {
            setActiveSection(sec.id);
            setActiveSub(sub.id);
            setExpanded((prev) => ({ ...prev, [sec.id]: true }));
            return;
          }
        }
      },
      {
        root: contentRef.current,
        rootMargin: "-10% 0px -60% 0px",
        threshold: 0,
      },
    );

    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [sections]);

  const scrollTo = useCallback((id, secId) => {
    const el = sectionRefs.current[id];
    if (!el || !contentRef.current) return;

    isScrollingTo.current = true;
    setActiveSection(secId || id);
    setActiveSub(id === secId ? null : id);
    setExpanded((prev) => ({ ...prev, [secId || id]: true }));
    setSidebarOpen(false);

    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      isScrollingTo.current = false;
    }, 800);
  }, []);

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const Sidebar = () => (
    <nav className="w-full">
     <div className="flex items-center gap-2 mb-4 px-2 border-b border-gray-800/60 pb-4">
  <button
    onClick={() => navigate(-1)}
    className="text-gray-500 hover:text-white transition-colors flex-shrink-0"
    aria-label="Go back"
  >
    <ArrowLeft size={16} />
  </button>
  <p className="text-xs font-semibold uppercase tracking-widest text-[#888]">
    Sections
  </p>
</div>
      {sections.map((sec) => {
        const isSecActive = activeSection === sec.id;
        const isExpanded = expanded[sec.id];

        return (
          <div key={sec.id} className="mb-1">
            {/* Section header */}
            <button
              onClick={() => {
                toggleExpand(sec.id);
                scrollTo(sec.id, sec.id);
              }}
              className={`w-full text-left px-3 py-2 rounded text-sm font-semibold uppercase tracking-wide transition-colors font-[Norwester] flex items-center justify-between
                ${
                  isSecActive && !activeSub
                    ? "text-white"
                    : "text-[#aaaaaa] hover:text-white"
                }`}
            >
              {sec.title}
            </button>

            {/* Subsections */}
            {isExpanded && sec.subsections && (
              <div className="ml-3 mt-0.5 space-y-0.5">
                {sec.subsections.map((sub) => {
                  const isSubActive =
                    activeSub === sub.id && activeSection === sec.id;
                  return (
                    <button
                      key={sub.id}
                      onClick={() => scrollTo(sub.id, sec.id)}
                      className={`w-full text-left px-3 py-1.5 text-sm rounded transition-colors flex items-center gap-1
                        ${
                          isSubActive
                            ? "text-[#FFF528]"
                            : "text-[#888888] hover:text-[#cccccc]"
                        }`}
                    >
                      {isSubActive && (
                        <span className="w-0.5 h-3.5 bg-[#FFF528] rounded-full mr-1 flex-shrink-0" />
                      )}
                      {sub.title}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  const renderSection = (sec) => {
    const secData = content[sec.id];
    if (!secData) return null;

    return (
      <div key={sec.id}>
        <div
          id={sec.id}
          ref={(el) => {
            sectionRefs.current[sec.id] = el;
          }}
          className="pt-2"
        >
          <h1 className="text-2xl font-bold text-white mb-4">
            {secData.title}
          </h1>
           <div className="mt-3 mb-9 h-px bg-gradient-to-r from-[#FFF528]/30 via-gray-700 to-transparent" />
          
          {secData.intro && (
            <p className="text-[#cccccc] text-sm leading-relaxed mb-4">
              {secData.intro}
            </p>
          )}
          <RenderBody body={secData.body} />
        </div>

        {sec.subsections?.map((sub) => {
          const subData = secData.subsections?.[sub.id];
          if (!subData) return null;
          return (
            <div
              key={sub.id}
              id={sub.id}
              ref={(el) => {
                sectionRefs.current[sub.id] = el;
              }}
              className="mt-8"
            >
              <h2 className="text-xl font-bold text-white mb-3">
                {subData.title}
              </h2>
              <RenderBody body={subData.body} />
            </div>
          );
        })}
      </div>
    );
  };


  const currentIdx = allAnchors.indexOf(activeSub || activeSection);
  const prevId = allAnchors[currentIdx - 1];
  const nextId = allAnchors[currentIdx + 1];

  const findSecForAnchor = (id) => {
    for (const sec of sections) {
      if (sec.id === id) return sec.id;
      if ((sec.subsections || []).find((s) => s.id === id)) return sec.id;
    }
    return id;
  };

return (
  <div className="flex bg-[#0E1011] font-sans" style={{ height: "100vh", overflow: "hidden" }}>

    {/* Mobile overlay */}
    {sidebarOpen && !isDesktop && (
      <div
        className="fixed inset-0 z-20 bg-black/60"
        onClick={() => setSidebarOpen(false)}
      />
    )}

    {/* Sidebar */}
    <aside className="dark-scrollbar"
      style={{
        position: isDesktop ? "relative" : "fixed",
        top: 0,
        left: 0,
        height: "100vh",
        width: "256px",
        zIndex: 30,
        background: "#0E1011",
        borderRight: "1px solid #222222",
        display: "flex",
        flexDirection: "column",
        padding: "24px 16px",
        overflowY: "auto",
        transform: isDesktop
          ? "translateX(0)"
          : sidebarOpen
          ? "translateX(0)"
          : "translateX(-100%)",
        transition: "transform 0.3s ease-in-out",
        flexShrink: 0,
        
      }}
    >
      {/* Close button — mobile only */}
      {!isDesktop && (
        <button
          onClick={() => setSidebarOpen(false)}
          className="text-[#888] hover:text-white mb-4 self-end"
          aria-label="Close menu"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      
      <Sidebar />
    </aside>

    {/* Main content */}
    <main
      ref={contentRef}
      className="flex-1 overflow-y-auto"
      style={{ height: "100vh" }}
    >
      {/* Mobile top bar */}
{!isDesktop && (
  <div className="flex items-center gap-3 px-4 py-3 border-b border-[#222222] flex-shrink-0 sticky top-0 bg-[#0E1011]" style={{ zIndex: 10 }}>
    <button
      onClick={() => navigate(-1)}
      className="text-gray-500 hover:text-white transition-colors"
      aria-label="Go back"
    >
      <ArrowLeft size={18} />
    </button>
    <span className="text-sm text-gray-400 font-semibold flex-1 truncate">
      {activeSub
        ? sections.flatMap(s => s.subsections || []).find(s => s.id === activeSub)?.title
        : sections.find(s => s.id === activeSection)?.title || "Broadcaster Guide"}
    </span>
    <button
      onClick={() => setSidebarOpen(true)}
      className="text-gray-400 hover:text-white transition-colors"
      aria-label="Open menu"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    </button>
  </div>
)}

      <div
        className=" mx-auto"
        style={{ padding: isDesktop ? "32px 40px" : "24px 16px" }}
      >
        <div className="space-y-16">
          {isDesktop && (
  <div className="mb-10">
    <p className="text-xs uppercase tracking-widest text-[#555] mb-1">Documentation</p>
    <h1 className="text-3xl font-bold text-white">Broadcaster Guide</h1>
  
  </div>
)}
          {sections.map((sec) => renderSection(sec))}
        </div>
      </div>
    </main>
  </div>
);
};

export default Docs;
